/**
 * DesignToolbar — the canonical "status + save + export" bar that lives
 * at the top of every design page.
 *
 * Look:
 *   ● Listo / Calculando…    [↓ PDF 2 / ∞ incluido]  [↓ XLSX 2 / ∞ incluido]  [Guardar]
 *
 * The export buttons use the orange (PDF) and blue (XLSX) palette
 * confirmed by design. The cost hint switches between "2 fierros" and
 * "∞ incluido" based on the user's subscription, courtesy of
 * `ExportCostHint`.
 */

import { Save, Download, FileSpreadsheet, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ExportCostHint } from '@/features/billing/ExportCostHint';

interface DesignToolbarProps {
  /** "ready" shows a green dot · "busy" shows a spinner · "error" shows red. */
  status: 'ready' | 'busy' | 'error';
  statusLabel?: string;

  onExportPdf: () => void;
  onExportXlsx: () => void;
  busyExport: 'pdf' | 'xlsx' | null;

  onSave: () => void;
  saving?: boolean;
  /** When true, the save button label is "Actualizar" instead of "Guardar". */
  isUpdate?: boolean;

  /** Token cost per export (defaults to 2, the standard for design exports). */
  costPdf?: number;
  costXlsx?: number;
}

export function DesignToolbar({
  status,
  statusLabel,
  onExportPdf,
  onExportXlsx,
  busyExport,
  onSave,
  saving,
  isUpdate,
  costPdf = 2,
  costXlsx = 2,
}: DesignToolbarProps) {
  const dotColor =
    status === 'ready' ? 'var(--success)' : status === 'busy' ? 'var(--accent)' : 'var(--danger)';
  const labelText =
    statusLabel ?? (status === 'ready' ? 'Listo' : status === 'busy' ? 'Calculando…' : 'Error');

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b px-6 py-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
    >
      <div className="flex items-center gap-2 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
        {status === 'busy' ? (
          <Loader2 size={12} className="animate-spin" style={{ color: dotColor }} />
        ) : (
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: dotColor }}
          />
        )}
        <span>{labelText}</span>
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {/* Orange outlined PDF button */}
        <button
          type="button"
          onClick={onExportPdf}
          disabled={busyExport !== null || status !== 'ready'}
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius)] border px-3 text-[12.5px] font-medium transition-colors hover:bg-[rgba(245,158,11,0.10)] disabled:cursor-not-allowed disabled:opacity-50"
          style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
          title="Descargar la memoria de cálculo en PDF"
        >
          {busyExport === 'pdf' ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Download size={13} strokeWidth={2} />
          )}
          PDF
          <ExportCostHint cost={costPdf} className="ml-1" />
        </button>

        {/* Blue outlined XLSX button */}
        <button
          type="button"
          onClick={onExportXlsx}
          disabled={busyExport !== null || status !== 'ready'}
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius)] border px-3 text-[12.5px] font-medium transition-colors hover:bg-[rgba(59,130,246,0.10)] disabled:cursor-not-allowed disabled:opacity-50"
          style={{ borderColor: '#3B82F6', color: '#3B82F6' }}
          title="Descargar el diseño en Excel editable"
        >
          {busyExport === 'xlsx' ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={13} strokeWidth={2} />
          )}
          XLSX
          <ExportCostHint cost={costXlsx} className="ml-1" />
        </button>

        {/* Save button (filled) */}
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={saving || status !== 'ready'}
          title={isUpdate ? 'Actualizar el diseño guardado' : 'Guardar este diseño'}
        >
          {saving ? (
            <Loader2 size={13} className="animate-spin" />
          ) : isUpdate ? (
            <CheckCircle2 size={13} strokeWidth={2} />
          ) : (
            <Save size={13} strokeWidth={2} />
          )}
          {isUpdate ? 'Actualizar' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}
